// client/src/utils/auth.js

/**
 * Decodifica manualmente el payload de un JWT.
 * - token debe tener el formato "header.payload.signature"
 * - Retorna el objeto JSON decodificado del payload, o null si falla.
 */
function decodeJwtPayload(token) {
  try {
    // 1) Separar en tres partes: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // 2) Tomar la segunda parte (payload) y decodificar Base64
    const payloadBase64 = parts[1]
      // En algunos entornos el base64url usa "_" y "-" en lugar de "/" y "+"
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    // atob convierte Base64 a cadena UTF-8
    const jsonStr = decodeURIComponent(
      Array.prototype.map
        .call(atob(payloadBase64), (c) =>
          '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        )
        .join('')
    );
    // 3) Parsear JSON
    return JSON.parse(jsonStr);
  } catch {
    return null;
  }
}

/**
 * Comprueba si hay un token válido en localStorage.
 * - Si no existe, devuelve false.
 * - Si existe, lo decodifica manualmente y compara exp (en segundos) con la hora actual.
 * - Si exp no existe o el token está vencido, borra localStorage y devuelve false.
 */
export function isTokenValid() {
  const token = localStorage.getItem('token');

  if (!token) {
    return false;
  }

  // Decodificamos sin usar librerías externas.
  const decoded = decodeJwtPayload(token);

  if (!decoded || typeof decoded.exp !== 'number') {
    localStorage.removeItem('token');
    return false;
  }

  const nowSecs = Date.now() / 1000;

  if (nowSecs > decoded.exp) {
    localStorage.removeItem('token');
    return false;
  }

  return true;
}

/**
 * Obtiene el userId del token JWT almacenado en localStorage.
 * @returns {string|null} - El userId o null si no hay token válido
 */
export function getUserIdFromToken() {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const decoded = decodeJwtPayload(token);
  return decoded?.userId || null;
}

/**
 * Obtiene el rol del usuario del token JWT almacenado en localStorage.
 * @returns {string|null} - El rol ('employee', 'manager', 'admin') o null si no hay token válido
 */
export function getUserRoleFromToken() {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const decoded = decodeJwtPayload(token);
  return decoded?.role || 'employee'; // Default a employee si no tiene rol
}

/**
 * Verifica si el usuario actual es manager o admin.
 * @returns {boolean} - true si es manager o admin, false si es employee o no hay token
 */
export function isManagerOrAdmin() {
  const role = getUserRoleFromToken();
  return role === 'manager' || role === 'admin';
}

/**
 * Verifica si el usuario actual es admin.
 * @returns {boolean} - true si es admin, false en cualquier otro caso
 */
export function isAdmin() {
  return getUserRoleFromToken() === 'admin';
}

